import React, { useEffect, useRef, useState } from 'react';
import Api from '@plone/volto/helpers/Api/Api';

const api = new Api();

const getCSRFToken = async () => {
  try {
    const response = await api.get('/@@authenticator/token');
    return response?.value || response;
  } catch {
    return null;
  }
};

const proposalTypes = ['Talk', 'Training', 'Sprint Topic'];
const talkLengthOptions = ['25 minutes', '45 minutes'];
const targetLevels = ['Beginner', 'Intermediate', 'Advanced'];
const targetAudienceOptions = [
  'Frontend',
  'Backend',
  'Integrator',
  'Business',
  'Other',
];
const languageOptions = ['English', 'Deutsch', 'Français'];

const RequiredLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor}>
    {children}
    <span aria-hidden="true" className="cfp-required-asterisk">
      *
    </span>
  </label>
);

const INITIAL_FORM = {
  website: '',
  email: '',
  title: '',
  speaker_name: '',
  speaker_introduction: '',
  co_speaker_name: '',
  co_speaker_introduction: '',
  proposal_type: '',
  abstract: '',
  talk_length: '',
  target_level: '',
  target_audience: [],
  language: '',
};

const SUCCESS_MESSAGE =
  'Thanks! Your proposal has been submitted. We will get in contact with you soon.';

const SubmissionSummary = ({ submission }) => {
  if (!submission) {
    return null;
  }
  const audience = (submission.target_audience || []).join(', ');
  const hasCoSpeaker = (submission.co_speaker_name || '').trim().length > 0;
  return (
    <section aria-label="Your submission" className="cfp-summary">
      <h3 className="cfp-summary-heading">Your submission</h3>
      <dl className="cfp-summary-list">
        <dt>Title</dt>
        <dd>{submission.title}</dd>
        {submission.proposal_type ? (
          <>
            <dt>Type</dt>
            <dd>{submission.proposal_type}</dd>
          </>
        ) : null}
        {submission.talk_length ? (
          <>
            <dt>Length</dt>
            <dd>{submission.talk_length}</dd>
          </>
        ) : null}
        {submission.target_level ? (
          <>
            <dt>Level</dt>
            <dd>{submission.target_level}</dd>
          </>
        ) : null}
        {audience ? (
          <>
            <dt>Audience</dt>
            <dd>{audience}</dd>
          </>
        ) : null}
        {submission.language ? (
          <>
            <dt>Language</dt>
            <dd>{submission.language}</dd>
          </>
        ) : null}
      </dl>
      <h4 className="cfp-summary-subheading">Speaker</h4>
      <p className="cfp-summary-name">{submission.speaker_name}</p>
      {submission.speaker_introduction ? (
        <p className="cfp-summary-prose">{submission.speaker_introduction}</p>
      ) : null}
      {hasCoSpeaker ? (
        <>
          <h4 className="cfp-summary-subheading">Co-speaker</h4>
          <p className="cfp-summary-name">{submission.co_speaker_name}</p>
          {submission.co_speaker_introduction ? (
            <p className="cfp-summary-prose">
              {submission.co_speaker_introduction}
            </p>
          ) : null}
        </>
      ) : null}
      <h4 className="cfp-summary-subheading">Abstract</h4>
      <p className="cfp-summary-prose">{submission.abstract}</p>
      <p className="cfp-summary-contact">
        We&apos;ll be in touch at <strong>{submission.email}</strong>.
      </p>
    </section>
  );
};

const CallForProposalForm = ({ className = '', readOnly = false }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submittedData, setSubmittedData] = useState(null);
  const wrapperRef = useRef(null);

  // After the form is replaced by the Thanks/summary view, scroll the
  // wrapper into view so the user actually sees the confirmation. Without
  // this, long forms leave the browser scrolled near the submit button
  // and the success message ends up above the viewport.
  useEffect(() => {
    if (success && wrapperRef.current) {
      wrapperRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [success]);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const onAudienceChange = (event) => {
    const { value, checked } = event.target;
    setForm((current) => {
      const nextAudience = checked
        ? [...current.target_audience, value]
        : current.target_audience.filter((item) => item !== value);
      return {
        ...current,
        target_audience: nextAudience,
      };
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    if (readOnly) {
      return;
    }

    if (form.website?.trim()) {
      setSubmittedData(form);
      setSuccess(SUCCESS_MESSAGE);
      setError('');
      return;
    }

    if (form.target_audience.length === 0) {
      setError('Please choose at least one target audience.');
      setSuccess('');
      return;
    }

    if (!form.email || !event.currentTarget.checkValidity()) {
      setError('Please provide a valid email address.');
      setSuccess('');
      return;
    }

    setError('');
    setSuccess('');
    setSending(true);

    try {
      const csrfToken = await getCSRFToken();
      const headers = {};
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
      const response = await api.post('/@cfp-submit', { data: form, headers });
      if (response?.success) {
        setSubmittedData(form);
        setSuccess(SUCCESS_MESSAGE);
        setForm(INITIAL_FORM);
      } else {
        setError(response?.message || 'Could not submit your proposal.');
      }
    } catch (submissionError) {
      setError(
        submissionError?.response?.body?.message ||
          'Could not submit your proposal. Please try again.',
      );
    } finally {
      setSending(false);
    }
  };

  if (success) {
    return (
      <div
        ref={wrapperRef}
        aria-live="polite"
        className={`cfp-form ${className}`.trim()}
      >
        <p className="cfp-message is-success" role="status">
          {success}
        </p>
        <SubmissionSummary submission={submittedData} />
      </div>
    );
  }

  return (
    <form
      ref={wrapperRef}
      className={`cfp-form ${className}`.trim()}
      onSubmit={onSubmit}
    >
      <div aria-hidden="true" className="cfp-honeypot" role="presentation">
        <label htmlFor="cfp-website">Website</label>
        <input
          autoComplete="off"
          id="cfp-website"
          name="website"
          onChange={onChange}
          tabIndex={-1}
          type="text"
          value={form.website}
        />
      </div>

      <RequiredLabel htmlFor="cfp-email">Your Email</RequiredLabel>
      <em>(we will contact you via this email)</em>
      <input
        id="cfp-email"
        name="email"
        onChange={onChange}
        pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
        placeholder="you@example.org"
        required
        type="email"
        value={form.email}
      />

      <RequiredLabel htmlFor="cfp-title">Title</RequiredLabel>
      <input
        id="cfp-title"
        name="title"
        onChange={onChange}
        placeholder="Example: Building Scalable Plone APIs"
        required
        type="text"
        value={form.title}
      />

      <RequiredLabel htmlFor="cfp-speaker-name">Speaker Name</RequiredLabel>
      <input
        id="cfp-speaker-name"
        name="speaker_name"
        onChange={onChange}
        placeholder="Your full name"
        required
        type="text"
        value={form.speaker_name}
      />

      <RequiredLabel htmlFor="cfp-speaker-introduction">
        Speaker Introduction
      </RequiredLabel>
      <textarea
        id="cfp-speaker-introduction"
        name="speaker_introduction"
        onChange={onChange}
        placeholder="A short bio, your role, and your background"
        required
        rows={4}
        value={form.speaker_introduction}
      />

      <label htmlFor="cfp-co-speaker-name">Co Speaker Name</label>
      <input
        id="cfp-co-speaker-name"
        name="co_speaker_name"
        onChange={onChange}
        placeholder="Optional co-speaker name"
        type="text"
        value={form.co_speaker_name}
      />

      <label htmlFor="cfp-co-speaker-introduction">
        Co Speaker Introduction
      </label>
      <textarea
        id="cfp-co-speaker-introduction"
        name="co_speaker_introduction"
        onChange={onChange}
        placeholder="Optional short bio for the co-speaker"
        rows={4}
        value={form.co_speaker_introduction}
      />

      <RequiredLabel htmlFor="cfp-proposal-type">
        Type of your proposal
      </RequiredLabel>
      <select
        id="cfp-proposal-type"
        name="proposal_type"
        onChange={onChange}
        required
        value={form.proposal_type}
      >
        <option value="">Select proposal type</option>
        {proposalTypes.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <RequiredLabel htmlFor="cfp-abstract">Abstract</RequiredLabel>
      <textarea
        id="cfp-abstract"
        name="abstract"
        onChange={onChange}
        placeholder="Summarize your proposal, key takeaways, and expected outcomes"
        required
        rows={8}
        value={form.abstract}
      />

      <RequiredLabel htmlFor="cfp-talk-length">Talk Length</RequiredLabel>
      <select
        id="cfp-talk-length"
        name="talk_length"
        onChange={onChange}
        required
        value={form.talk_length}
      >
        <option value="">Select talk length</option>
        {talkLengthOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <RequiredLabel htmlFor="cfp-target-level">Target Level</RequiredLabel>
      <select
        id="cfp-target-level"
        name="target_level"
        onChange={onChange}
        required
        value={form.target_level}
      >
        <option value="">Select target level</option>
        {targetLevels.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <fieldset className="cfp-choice-group" required>
        <legend>
          Target Audience (click all that apply)
          <span aria-hidden="true" className="cfp-required-asterisk">
            *
          </span>
        </legend>
        {targetAudienceOptions.map((option) => {
          const optionId = option.toLowerCase().replace(/\s+/g, '-');
          return (
            <label
              key={option}
              className="cfp-choice-item"
              htmlFor={`cfp-audience-${optionId}`}
            >
              <input
                checked={form.target_audience.includes(option)}
                id={`cfp-audience-${optionId}`}
                name="target_audience"
                onChange={onAudienceChange}
                type="checkbox"
                value={option}
              />
              {option}
            </label>
          );
        })}
      </fieldset>

      <fieldset className="cfp-choice-group" required>
        <legend>
          Language
          <span aria-hidden="true" className="cfp-required-asterisk">
            *
          </span>
        </legend>
        {languageOptions.map((option) => {
          const optionId = option.toLowerCase().replace(/\s+/g, '-');
          return (
            <label
              key={option}
              className="cfp-choice-item"
              htmlFor={`cfp-language-${optionId}`}
            >
              <input
                checked={form.language === option}
                id={`cfp-language-${optionId}`}
                name="language"
                onChange={onChange}
                required
                type="radio"
                value={option}
              />
              {option}
            </label>
          );
        })}
      </fieldset>

      {error ? <p className="cfp-message is-error">{error}</p> : null}
      <button className="button" disabled={sending || readOnly} type="submit">
        {readOnly
          ? 'Save page to publish this form'
          : sending
            ? 'Submitting...'
            : 'Submit proposal'}
      </button>
    </form>
  );
};

export default CallForProposalForm;
